import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

/** Rows currently being dragged, and the row hovered as a drop target. */
export type ListDragState = {
  draggingIndices: number[];
  dropTargetIndex: number | null;
};

/** Distance from a scroll edge at which a hovering drag starts scrolling. */
const EDGE_ZONE_PX = 72;
/** Scroll speed at the very edge; ramps up across the edge zone. */
const MAX_SPEED_PX = 18;

export type ResourceListDrag = {
  /** Null when no drag is in progress. */
  dragState: ListDragState | null;
  onDragStart: (index: number, event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (index: number, event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (index: number, event: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
};

/**
 * Row drag-and-drop for the resource list: drag state, the custom drag image,
 * edge auto-scroll, and drop validation.
 *
 * Extracted from `ResourceListView` — it's the one wholly self-contained
 * subsystem in that component, and it owns four refs, a piece of state, an
 * animation loop and a React root that have nothing to do with rendering rows.
 */
export function useResourceListDrag<T>(params: {
  /** Rows in render order; indices in the returned handlers refer to these. */
  items: T[];
  /** Currently selected indices — dragging a selected row carries them all. */
  selectedIndices: number[];
  selectedIndexSet: ReadonlySet<number>;
  /** Whether a row can receive a drop. Omit to accept none. */
  canDropOnItem?: (item: T) => boolean;
  /** Drop handler. Drag-and-drop is inert until this is provided. */
  onDropOnItem?: (target: T, draggedItems: T[]) => void;
  /** Rendered under the cursor while dragging. */
  renderDragImage?: (draggedItems: T[]) => React.ReactNode;
  /** The element that scrolls, for edge auto-scroll. */
  resolveScrollElement: () => HTMLElement | null;
  /** Host for the offscreen drag-image root, so themed CSS variables resolve. */
  dragImageHostRef: React.RefObject<HTMLElement | null>;
  /** Called after a successful drop (the list clears its selection). */
  onDropCompleted: () => void;
}): ResourceListDrag {
  const {
    items,
    selectedIndices,
    selectedIndexSet,
    canDropOnItem,
    onDropOnItem,
    renderDragImage,
    resolveScrollElement,
    dragImageHostRef,
    onDropCompleted,
  } = params;

  const [dragState, setDragState] = useState<ListDragState | null>(null);
  const dragImageCleanupRef = useRef<(() => void) | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const scrollSpeedRef = useRef(0);

  const stopAutoScroll = useCallback(() => {
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
    scrollSpeedRef.current = 0;
  }, []);

  /**
   * Scrolls while a drag hovers near an edge, ramping with depth into the zone.
   * A single rAF loop reads its speed from a ref, so the continuous `dragover`
   * stream only adjusts speed rather than stacking loops.
   */
  const updateAutoScroll = useCallback(
    (clientY: number) => {
      const scrollEl = resolveScrollElement();
      if (scrollEl === null) {
        stopAutoScroll();
        return;
      }
      const rect = scrollEl.getBoundingClientRect();
      const intoTop = rect.top + EDGE_ZONE_PX - clientY;
      const intoBottom = clientY - (rect.bottom - EDGE_ZONE_PX);
      const speed =
        intoTop > 0
          ? -MAX_SPEED_PX * Math.min(1, intoTop / EDGE_ZONE_PX)
          : intoBottom > 0
          ? MAX_SPEED_PX * Math.min(1, intoBottom / EDGE_ZONE_PX)
          : 0;
      scrollSpeedRef.current = speed;
      if (speed === 0) {
        stopAutoScroll();
        return;
      }
      if (scrollRafRef.current !== null) {
        return;
      }
      const step = (): void => {
        const target = resolveScrollElement();
        const currentSpeed = scrollSpeedRef.current;
        if (target === null || currentSpeed === 0) {
          scrollRafRef.current = null;
          return;
        }
        target.scrollBy(0, currentSpeed);
        scrollRafRef.current = requestAnimationFrame(step);
      };
      scrollRafRef.current = requestAnimationFrame(step);
    },
    [resolveScrollElement, stopAutoScroll]
  );

  const isValidDropTarget = useCallback(
    (index: number): boolean => {
      if (dragState === null || canDropOnItem === undefined) {
        return false;
      }
      if (dragState.draggingIndices.includes(index)) {
        return false;
      }
      const item = items[index];
      return item !== undefined && canDropOnItem(item);
    },
    [dragState, canDropOnItem, items]
  );

  const onDragStart = useCallback(
    (index: number, event: React.DragEvent<HTMLDivElement>) => {
      // Drag the whole selection when the grabbed row is part of it; otherwise
      // drag just that row (Finder-style).
      const draggingIndices =
        selectedIndexSet.has(index) && selectedIndices.length > 0
          ? [...selectedIndices]
          : [index];
      setDragState({ draggingIndices, dropTargetIndex: null });
      event.dataTransfer.effectAllowed = 'move';
      // Firefox won't start a drag unless some data is set.
      event.dataTransfer.setData('text/plain', '');

      if (renderDragImage === undefined) {
        return;
      }
      const draggedItems = draggingIndices
        .map((i) => items[i])
        .filter((item): item is T => item !== undefined);
      if (draggedItems.length === 0) {
        return;
      }
      // Render the caller's node offscreen (inside the themed subtree so CSS
      // variables resolve), snapshot it as the drag image, then tear it down.
      dragImageCleanupRef.current?.();
      const host = document.createElement('div');
      host.style.cssText =
        'position:fixed;top:-10000px;left:-10000px;pointer-events:none;z-index:-1;';
      (dragImageHostRef.current ?? document.body).appendChild(host);
      const root = createRoot(host);
      flushSync(() => {
        root.render(renderDragImage(draggedItems));
      });
      event.dataTransfer.setDragImage(host, 12, 12);
      dragImageCleanupRef.current = () => {
        root.unmount();
        host.remove();
        dragImageCleanupRef.current = null;
      };
    },
    [
      selectedIndexSet,
      selectedIndices,
      renderDragImage,
      items,
      dragImageHostRef,
    ]
  );

  const onDragOver = useCallback(
    (index: number, event: React.DragEvent<HTMLDivElement>) => {
      // Auto-scroll runs whether or not this row accepts the drop — the point is
      // to reach a row that does.
      updateAutoScroll(event.clientY);
      // Cleared here rather than in `dragleave`, which fires spuriously as the
      // cursor crosses a row's child cells and would flicker the highlight.
      if (!isValidDropTarget(index)) {
        setDragState((prev) =>
          prev === null || prev.dropTargetIndex === null
            ? prev
            : { ...prev, dropTargetIndex: null }
        );
        return;
      }
      // preventDefault marks this row as a drop target for the browser.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDragState((prev) =>
        prev === null || prev.dropTargetIndex === index
          ? prev
          : { ...prev, dropTargetIndex: index }
      );
    },
    [isValidDropTarget, updateAutoScroll]
  );

  const onDrop = useCallback(
    (index: number, event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      stopAutoScroll();
      if (dragState === null) {
        return;
      }
      const target = items[index];
      const shouldDrop = isValidDropTarget(index) && target !== undefined;
      const draggedItems = shouldDrop
        ? dragState.draggingIndices
            .map((i) => items[i])
            .filter((item): item is T => item !== undefined)
        : [];
      setDragState(null);
      if (shouldDrop && target !== undefined && draggedItems.length > 0) {
        onDropOnItem?.(target, draggedItems);
        // A destination was chosen — leave multi-select. The drop handler
        // already holds the dragged items, so clearing doesn't affect the
        // in-flight move.
        onDropCompleted();
      }
    },
    [
      dragState,
      items,
      isValidDropTarget,
      onDropOnItem,
      onDropCompleted,
      stopAutoScroll,
    ]
  );

  const onDragEnd = useCallback(() => {
    setDragState(null);
    stopAutoScroll();
    dragImageCleanupRef.current?.();
  }, [stopAutoScroll]);

  // Tear down a lingering drag image / scroll loop if the list unmounts mid-drag.
  useEffect(
    () => () => {
      dragImageCleanupRef.current?.();
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    },
    []
  );

  return { dragState, onDragStart, onDragOver, onDrop, onDragEnd };
}
